import type { ComponentProps } from 'react';
import { Fragment } from 'react';
import { Text, View, XStack, YStack } from 'tamagui';

import PSIcon from './ui/PSIcon';
import PSText from './ui/PSText';

/**
 * SettingsGroup — a vertical group of connected, clickable rows (molecule).
 *
 * The settings-page pattern: rows stacked into a single recessed
 * `$backgroundSecondary` surface, hairline dividers between them, and the group's
 * outer corners rounded while the joins between rows stay square — so the whole
 * thing reads as ONE card rather than N separate boxes. A screen can render
 * several of these groups down the page (e.g. "Account", "Preferences",
 * "About"), each an island of related actions.
 *
 * Built fully from our primitives, NOT Tamagui's `YGroup` / `ListItem`. Those
 * derive padding + radius from the `size` token group, which our config can't
 * type-check (our custom `space` scale breaks the size/space contract — see
 * constants/tokens.ts), and `YGroup`'s only way to round the card is
 * `overflow="hidden"`, which clips the last row and shaves a pixel off its
 * height. We sidestep both: the ROWS own the corner rounding — the first row
 * rounds its top corners, the last its bottom — so the card reads as one rounded
 * unit with NO clipping (nothing is cut, so no height loss). Every value is one
 * of our `$tokens`, matching the `TicketListItem` recessed-box idiom.
 */

export type SettingsGroupItem = {
  /** Ionicons glyph name (via PSIcon), e.g. "person-outline". */
  icon: ComponentProps<typeof PSIcon>['name'];
  label: string;
  onPress?: () => void;
  /** Danger styling for destructive rows (e.g. "Log out", "Delete account"). */
  destructive?: boolean;
};

/** A single row. `first`/`last` round the matching corners so the stack of rows
 *  reads as one rounded card — done per-row (not via a clipping parent) so the
 *  last row keeps its full height. A one-item group is both first and last. */
function SettingsRow({
  icon,
  label,
  onPress,
  destructive,
  first,
  last,
}: SettingsGroupItem & { first: boolean; last: boolean }) {
  // Destructive rows tint the icon + label red; the chevron stays neutral so
  // only the action itself reads as dangerous, not the navigation affordance.
  const tint = destructive ? '$error' : '$textDark';
  return (
    <XStack
      items="center"
      gap="$md"
      px="$lg"
      py="$lg"
      bg="$backgroundSecondary"
      // Only the outer corners of the whole group round; interior joins stay
      // square. Rounding each end row's own corners (vs clipping a parent) is
      // what keeps the last row at full height. Per-corner longhands because
      // there's no roundedTop/Bottom shorthand.
      borderTopLeftRadius={first ? '$base' : 0}
      borderTopRightRadius={first ? '$base' : 0}
      borderBottomLeftRadius={last ? '$base' : 0}
      borderBottomRightRadius={last ? '$base' : 0}
      cursor={onPress ? 'pointer' : undefined}
      onPress={onPress}
      // Momentary press feedback — the surface darkens slightly on tap, matching
      // a native list row. No hover state (this is a touch surface).
      pressStyle={{ bg: '$borderColor' }}
    >
      <PSIcon name={icon} color={tint} />
      <Text flex={1} fontSize="$base" fontWeight="$medium" color={tint}>
        {label}
      </Text>
      <PSIcon name="chevron-forward" color="$textDarkMuted" size={18} />
    </XStack>
  );
}

export type SettingsGroupProps = {
  /** Optional section heading shown above the group (e.g. "Account"). */
  title?: string;
  items: SettingsGroupItem[];
};

export default function SettingsGroup({ title, items }: SettingsGroupProps) {
  return (
    <YStack gap="$sm">
      {title ? (
        <PSText.H2
        // The heading sits inset from the card edge so it aligns with the row
        // labels' left padding, reading as this group's title.
        // px="$lg"
        >
          {title}
        </PSText.H2>
      ) : null}
      {/* The rows carry the rounding themselves (see SettingsRow), so the
          container is a plain stack — no overflow clipping, no radius of its own.
          Hairline `$borderColor` Separators sit BETWEEN rows only (not above the
          first / below the last), so the card reads as one connected unit. */}
      <YStack>
        {items.map((item, i) => (
          <Fragment key={item.label}>
            {i > 0 ? (
              <View
                borderBottomWidth="$borderWidth.hairline"
                borderColor="$borderColor"
              />
            ) : null}
            <SettingsRow
              {...item}
              first={i === 0}
              last={i === items.length - 1}
            />
          </Fragment>
        ))}
      </YStack>
    </YStack>
  );
}
